import * as THREE from './three.module.min.js';

function setupTextureFit(texture, planeWidth, planeHeight, fit) {
  const image = texture.image;
  if (!image || !image.width || !image.height) return;

  const imageRatio = image.width / image.height;
  const planeRatio = planeWidth / planeHeight;

  texture.wrapS = texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.repeat.set(1, 1);
  texture.offset.set(0, 0);

  if (fit === 'cover') {
    if (imageRatio > planeRatio) {
      const scale = planeRatio / imageRatio;
      texture.repeat.set(scale, 1);
      texture.offset.set((1 - scale) * 0.5, 0);
    } else {
      const scale = imageRatio / planeRatio;
      texture.repeat.set(1, scale);
      texture.offset.set(0, (1 - scale) * 0.5);
    }
  }
}

function normalizeSrc(src) {
  if (!src) return '';
  if (src.startsWith('//')) return 'https:' + src;
  return src;
}

function initSection(sectionEl) {
  if (!sectionEl || sectionEl.dataset.canvasReady === 'true') return;

  const root = sectionEl.querySelector('[data-infinite-canvas-root]');
  const jsonEl = sectionEl.querySelector('[data-infinite-canvas-items]');
  if (!root || !jsonEl) return;

  let items = [];
  try {
    items = JSON.parse(jsonEl.textContent || '[]');
  } catch (e) {
    items = [];
  }
  if (!items.length) return;

  const invert = sectionEl.dataset.invertControls === 'true';
  const fit = sectionEl.dataset.imageFit || 'contain';
  const shell = sectionEl.querySelector('.infinite-canvas-shell');
  const isTouchDevice =
    window.matchMedia('(pointer: coarse)').matches ||
    ('ontouchstart' in window);

  const CONFIG = {
    CHUNK_SIZE: 1000,
    RENDER_DISTANCE: 1,
    CHUNK_FADE_MARGIN: 0.9,
    DEPTH_FADE_START: 760,
    DEPTH_FADE_END: 1380,
    INVIS_THRESHOLD: 0.02,
    PLANES_PER_CHUNK: 4,
    CAMERA_Z_BASE: 1150,
    DRAG: isTouchDevice ? 0.32 : 0.12,
    WHEEL: 0.1,
    ZOOM_SPEED: isTouchDevice ? 0.00016 : 0.00022,
    INPUT_DAMP: isTouchDevice ? 0.94 : 0.9,
    VEL_LERP: isTouchDevice ? 0.2 : 0.08,
    VEL_DECAY: isTouchDevice ? 0.992 : 0.985,
    LAYER_SPACING: 280,
    PLACEHOLDER_W: 320,
    PLACEHOLDER_H: 420
  };

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(
    34,
    root.clientWidth / root.clientHeight,
    0.1,
    10000
  );
  camera.position.set(0, 0, CONFIG.CAMERA_Z_BASE);

  const renderer = new THREE.WebGLRenderer({
    antialias: false,
    alpha: false,
    powerPreference: 'high-performance'
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.1));
  renderer.setSize(root.clientWidth, root.clientHeight);
  renderer.setClearColor(0xf6f4ef, 1);
  root.appendChild(renderer.domElement);

  scene.add(new THREE.AmbientLight(0xffffff, 1.0));

  const loader = new THREE.TextureLoader();
  loader.setCrossOrigin('anonymous');

  const state = {
    basePos: new THREE.Vector3(),
    targetVel: new THREE.Vector3(),
    velocity: new THREE.Vector3(),
    zoom: 1,
    targetZoom: 1,
    drag: false,
    lastX: 0,
    lastY: 0,
    keys: new Set(),
    raf: 0
  };

  const sharedGeometry = new THREE.PlaneGeometry(1, 1);
  const textureCache = new Map();
  const chunkMap = new Map();
  const pool = [];
  const offsets = [];

  for (let x = -1; x <= 1; x++) {
    for (let y = -1; y <= 1; y++) {
      for (let z = -1; z <= 1; z++) {
        offsets.push({ x, y, z, d: Math.hypot(x, y, z) });
      }
    }
  }
  offsets.sort((a, b) => a.d - b.d);

  function hash(str) {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  function rnd(seed) {
    const x = Math.sin(seed * 12.9898) * 43758.5453123;
    return x - Math.floor(x);
  }

  function intersects(a, b) {
    return !(
      a.x + a.w * 0.5 < b.x - b.w * 0.5 ||
      a.x - a.w * 0.5 > b.x + b.w * 0.5 ||
      a.y + a.h * 0.5 < b.y - b.h * 0.5 ||
      a.y - a.h * 0.5 > b.y + b.h * 0.5
    );
  }

  function createPlaceholderTexture() {
    const c = document.createElement('canvas');
    c.width = CONFIG.PLACEHOLDER_W;
    c.height = CONFIG.PLACEHOLDER_H;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#e8e1d4';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.fillStyle = '#d9ceb9';
    ctx.fillRect(16, 16, c.width - 32, c.height - 32);
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }

  const placeholderTexture = createPlaceholderTexture();

  function getTexture(src, onReady) {
    const url = normalizeSrc(src);
    if (!url) {
      onReady(placeholderTexture);
      return;
    }

    const cached = textureCache.get(url);
    if (cached && cached.status === 'ready') {
      onReady(cached.texture);
      return;
    }

    if (cached && cached.status === 'loading') {
      cached.waiters.push(onReady);
      return;
    }

    textureCache.set(url, {
      status: 'loading',
      texture: null,
      waiters: [onReady]
    });

    loader.load(
      url,
      (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        const entry = textureCache.get(url);
        if (!entry) return;
        entry.status = 'ready';
        entry.texture = texture;
        const waiters = entry.waiters.slice();
        entry.waiters.length = 0;
        waiters.forEach((fn) => fn(texture));
      },
      undefined,
      () => {
        const entry = textureCache.get(url);
        if (!entry) return;
        entry.status = 'error';
        const waiters = entry.waiters.slice();
        entry.waiters.length = 0;
        waiters.forEach((fn) => fn(placeholderTexture));
      }
    );
  }

  function makeMesh() {
    const material = new THREE.MeshBasicMaterial({
      map: placeholderTexture,
      transparent: true,
      opacity: 0,
      side: THREE.DoubleSide,
      depthTest: true,
      depthWrite: false
    });

    const mesh = new THREE.Mesh(sharedGeometry, material);
    mesh.visible = false;
    mesh.frustumCulled = true;
    scene.add(mesh);
    return mesh;
  }

  function getMeshFromPool() {
    return pool.pop() || makeMesh();
  }

  function releaseMesh(mesh) {
    mesh.visible = false;
    mesh.material.opacity = 0;
    mesh.material.map = placeholderTexture;
    mesh.material.needsUpdate = true;
    mesh.scale.set(1, 1, 1);
    mesh.position.set(0, 0, 0);
    mesh.rotation.set(0, 0, 0);
    mesh.userData = {};
    pool.push(mesh);
  }

  function generateChunkPlanes(cx, cy, cz) {
    const seed = hash(cx + ',' + cy + ',' + cz);
    const defs = [];
    const occupied = [];

    for (let i = 0; i < CONFIG.PLANES_PER_CHUNK; i++) {
      const item =
        items[Math.floor(rnd(seed + i * 19 + 5) * items.length)] || items[0];
      const ratio = (item.width || 1200) / Math.max(item.height || 1600, 1);
      const baseH = 120 + rnd(seed + i * 19 + 3) * 90;
      const baseW = baseH * ratio;
      const w = Math.max(80, Math.min(baseW, 240));
      const h = Math.max(100, Math.min(baseH, 280));

      let x = cx * CONFIG.CHUNK_SIZE;
      let y = cy * CONFIG.CHUNK_SIZE;
      let rect = null;

      for (let a = 0; a < 80; a++) {
        const tx =
          cx * CONFIG.CHUNK_SIZE +
          (rnd(seed + i * 97 + a * 2 + 10) - 0.5) *
            (CONFIG.CHUNK_SIZE - w - 180);
        const ty =
          cy * CONFIG.CHUNK_SIZE +
          (rnd(seed + i * 97 + a * 2 + 11) - 0.5) *
            (CONFIG.CHUNK_SIZE - h - 180);
        const cand = { x: tx, y: ty, w: w + 120, h: h + 120 };
        if (!occupied.some((r) => intersects(cand, r))) {
          x = tx;
          y = ty;
          rect = cand;
          break;
        }
      }

      if (!rect) rect = { x, y, w: w + 120, h: h + 120 };
      occupied.push(rect);

      defs.push({
        x,
        y,
        z:
          cz * CONFIG.CHUNK_SIZE +
          (i - (CONFIG.PLANES_PER_CHUNK - 1) * 0.5) * CONFIG.LAYER_SPACING +
          (rnd(seed + i * 19 + 4) - 0.5) * 36,
        w,
        h,
        item,
        chunkX: cx,
        chunkY: cy,
        chunkZ: cz,
        opacity: 0,
        key: cx + ',' + cy + ',' + cz + ':' + i
      });
    }

    return defs;
  }

  function activateChunk(cx, cy, cz) {
    const key = cx + ',' + cy + ',' + cz;
    if (chunkMap.has(key)) return;

    const meshes = generateChunkPlanes(cx, cy, cz).map((d) => {
      const mesh = getMeshFromPool();
      mesh.visible = true;
      mesh.position.set(d.x, d.y, d.z);
      mesh.scale.set(d.w, d.h, 1);
      mesh.rotation.set(0, 0, 0);
      mesh.userData = d;
      mesh.material.opacity = 0;
      mesh.material.map = placeholderTexture;
      mesh.material.needsUpdate = true;

      const src = normalizeSrc(d.item.src);
      if (src) {
        getTexture(src, (texture) => {
          if (!mesh.userData || mesh.userData.key !== d.key) return;
          mesh.material.map = texture;
          mesh.material.needsUpdate = true;
          setupTextureFit(texture, d.w, d.h, fit);
        });
      }

      return mesh;
    });

    chunkMap.set(key, meshes);
  }

  function deactivateChunk(key) {
    const meshes = chunkMap.get(key);
    if (!meshes) return;
    meshes.forEach(releaseMesh);
    chunkMap.delete(key);
  }

  function updateChunks() {
    const cx = Math.floor(state.basePos.x / CONFIG.CHUNK_SIZE);
    const cy = Math.floor(state.basePos.y / CONFIG.CHUNK_SIZE);
    const cz = Math.floor(state.basePos.z / CONFIG.CHUNK_SIZE);
    const needed = new Set();

    offsets.forEach((off) => {
      const nx = cx + off.x;
      const ny = cy + off.y;
      const nz = cz + off.z;
      const key = nx + ',' + ny + ',' + nz;
      needed.add(key);
      activateChunk(nx, ny, nz);
    });

    for (const key of chunkMap.keys()) {
      if (!needed.has(key)) deactivateChunk(key);
    }
  }

  function updateKeyboard() {
    const a = 0.38;
    if (invert) {
      if (state.keys.has('arrowleft') || state.keys.has('a')) state.targetVel.x -= a;
      if (state.keys.has('arrowright') || state.keys.has('d')) state.targetVel.x += a;
      if (state.keys.has('arrowup') || state.keys.has('w')) state.targetVel.y -= a;
      if (state.keys.has('arrowdown') || state.keys.has('s')) state.targetVel.y += a;
    } else {
      if (state.keys.has('arrowleft') || state.keys.has('a')) state.targetVel.x += a;
      if (state.keys.has('arrowright') || state.keys.has('d')) state.targetVel.x -= a;
      if (state.keys.has('arrowup') || state.keys.has('w')) state.targetVel.y += a;
      if (state.keys.has('arrowdown') || state.keys.has('s')) state.targetVel.y -= a;
    }
  }

  function onPointerDown(e) {
    if (e.cancelable) e.preventDefault();
    state.drag = true;
    state.lastX = e.clientX;
    state.lastY = e.clientY;
    if (shell) shell.classList.add('is-dragging');
    renderer.domElement.setPointerCapture(e.pointerId);
  }

  function onPointerMove(e) {
    if (!state.drag) return;
    if (e.cancelable) e.preventDefault();

    const dx = e.clientX - state.lastX;
    const dy = e.clientY - state.lastY;
    const dragBoost = isTouchDevice ? 1.35 : 1;

    if (invert) {
      state.targetVel.x -= dx * CONFIG.DRAG * dragBoost;
      state.targetVel.y += dy * CONFIG.DRAG * dragBoost;
    } else {
      state.targetVel.x += dx * CONFIG.DRAG * dragBoost;
      state.targetVel.y -= dy * CONFIG.DRAG * dragBoost;
    }

    state.lastX = e.clientX;
    state.lastY = e.clientY;
  }

  function onPointerUp() {
    state.drag = false;
    if (shell) shell.classList.remove('is-dragging');
  }

  function onWheel(e) {
    e.preventDefault();
    state.targetVel.z += e.deltaY * CONFIG.WHEEL;
    state.targetZoom = THREE.MathUtils.clamp(
      state.targetZoom + e.deltaY * -CONFIG.ZOOM_SPEED,
      0.9,
      1.15
    );
  }

  function onResize() {
    const w = root.clientWidth || 1;
    const h = root.clientHeight || 1;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  }

  function onKeyDown(e) {
    state.keys.add(e.key.toLowerCase());
  }

  function onKeyUp(e) {
    state.keys.delete(e.key.toLowerCase());
  }

  renderer.domElement.style.touchAction = 'none';

  renderer.domElement.addEventListener('pointerdown', onPointerDown);
  renderer.domElement.addEventListener('pointermove', onPointerMove);
  renderer.domElement.addEventListener('pointerup', onPointerUp);
  renderer.domElement.addEventListener('pointercancel', onPointerUp);
  renderer.domElement.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('resize', onResize);
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);

  function animate() {
    updateKeyboard();

    state.velocity.lerp(state.targetVel, CONFIG.VEL_LERP);
    state.targetVel.multiplyScalar(CONFIG.INPUT_DAMP);
    state.velocity.multiplyScalar(CONFIG.VEL_DECAY);
    state.basePos.add(state.velocity);
    state.zoom = THREE.MathUtils.lerp(state.zoom, state.targetZoom, 0.08);

    camera.position.set(
      state.basePos.x,
      state.basePos.y,
      CONFIG.CAMERA_Z_BASE + state.basePos.z
    );
    camera.zoom = state.zoom;
    camera.updateProjectionMatrix();

    updateChunks();

    const cam = {
      cx: Math.floor(state.basePos.x / CONFIG.CHUNK_SIZE),
      cy: Math.floor(state.basePos.y / CONFIG.CHUNK_SIZE),
      cz: Math.floor(state.basePos.z / CONFIG.CHUNK_SIZE),
      camZ: state.basePos.z
    };

    for (const arr of chunkMap.values()) {
      for (const mesh of arr) {
        const d = mesh.userData;
        const dist = Math.max(
          Math.abs(d.chunkX - cam.cx),
          Math.abs(d.chunkY - cam.cy),
          Math.abs(d.chunkZ - cam.cz)
        );
        const absDepth = Math.abs(d.z - cam.camZ);
        const gridFade =
          dist <= CONFIG.RENDER_DISTANCE
            ? 1
            : Math.max(
                0,
                1 -
                  (dist - CONFIG.RENDER_DISTANCE) /
                    Math.max(CONFIG.CHUNK_FADE_MARGIN, 0.0001)
              );
        const depthFade =
          absDepth <= CONFIG.DEPTH_FADE_START
            ? 1
            : Math.max(
                0,
                1 -
                  (absDepth - CONFIG.DEPTH_FADE_START) /
                    Math.max(
                      CONFIG.DEPTH_FADE_END - CONFIG.DEPTH_FADE_START,
                      0.0001
                    )
              );
        const target = Math.min(gridFade, depthFade * depthFade);

        d.opacity =
          target < CONFIG.INVIS_THRESHOLD && d.opacity < CONFIG.INVIS_THRESHOLD
            ? 0
            : THREE.MathUtils.lerp(d.opacity, target, 0.16);

        mesh.material.opacity = d.opacity;
        mesh.material.depthWrite = d.opacity > 0.96;
        mesh.visible = d.opacity > CONFIG.INVIS_THRESHOLD;
      }
    }

    renderer.render(scene, camera);
    state.raf = window.requestAnimationFrame(animate);
  }

  function destroy() {
    window.cancelAnimationFrame(state.raf);
    renderer.domElement.removeEventListener('pointerdown', onPointerDown);
    renderer.domElement.removeEventListener('pointermove', onPointerMove);
    renderer.domElement.removeEventListener('pointerup', onPointerUp);
    renderer.domElement.removeEventListener('pointercancel', onPointerUp);
    renderer.domElement.removeEventListener('wheel', onWheel);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);

    for (const key of Array.from(chunkMap.keys())) {
      deactivateChunk(key);
    }

    pool.forEach((mesh) => {
      scene.remove(mesh);
      mesh.material.dispose();
    });
    pool.length = 0;

    textureCache.forEach((entry) => {
      if (entry.texture && entry.texture !== placeholderTexture) {
        entry.texture.dispose();
      }
    });

    placeholderTexture.dispose();
    sharedGeometry.dispose();
    renderer.dispose();
    root.innerHTML = '';
    sectionEl.dataset.canvasReady = 'false';
  }

  sectionEl.__infiniteCanvasDestroy = destroy;
  sectionEl.dataset.canvasReady = 'true';
  animate();
}

function boot(container) {
  const sections = (container || document).querySelectorAll('.infinite-canvas-section');
  sections.forEach(initSection);
}

document.addEventListener('DOMContentLoaded', function () {
  boot(document);
});

document.addEventListener('shopify:section:load', function (event) {
  boot(event.target);
});

document.addEventListener('shopify:section:unload', function (event) {
  const section = event.target && event.target.querySelector
    ? event.target.querySelector('.infinite-canvas-section') || event.target
    : null;

  if (section && section.__infiniteCanvasDestroy) {
    section.__infiniteCanvasDestroy();
  }
});